import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import ReactDOM from "react-dom";

import {
  FiRotateCcw,
  FiX,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";

import {
  config,
} from "../../../../../../utils/apiUrl";

import {
  SALARY_PAYMENT_MODES,
} from "../utils/employee.constants";

import {
  formatEmployeeCurrency,
  formatEmployeeLabel,
  getEmployeeApiError,
} from "../utils/employee.utils";

import "./EmployeeAdvance.css";


const AdvanceRecoveryModal = ({
  open,

  advance,

  onClose,

  onSuccess,
}) => {

  const api =
    useMemo(
      () =>
        new API(),
      []
    );


  const [
    form,
    setForm,
  ] =
    useState({
      amount: "",
      recoveryDate: "",
      recoveryMode: "CASH",
      referenceNumber: "",
      remark: "",
    });


  const [
    loading,
    setLoading,
  ] =
    useState(false);


  const [
    error,
    setError,
  ] =
    useState("");


  const advanceAmount =
    Number(
      advance?.advanceAmount ??
      advance?.amount ??
      0
    );


  const recoveredAmount =
    Number(
      advance?.recoveredAmount ??
      advance?.totalRecoveredAmount ??
      0
    );


  const balanceAmount =
    Number(
      advance?.balanceAmount ??
      Math.max(
        advanceAmount -
        recoveredAmount,
        0
      )
    );


  useEffect(
    () => {

      if (
        !open
      ) {

        return;

      }


      const currentAdvance =
        Number(
          advance?.advanceAmount ??
          advance?.amount ??
          0
        );


      const currentRecovered =
        Number(
          advance?.recoveredAmount ??
          advance?.totalRecoveredAmount ??
          0
        );


      const balance =
        Number(
          advance?.balanceAmount ??
          Math.max(
            currentAdvance -
            currentRecovered,
            0
          )
        );


      setForm({
        amount:
          String(
            balance >
              0
              ?
              balance
              :
              ""
          ),

        recoveryDate:
          new Date()
            .toISOString()
            .slice(
              0,
              10
            ),

        recoveryMode:
          "CASH",

        referenceNumber:
          "",

        remark:
          "",
      });


      setError(
        ""
      );

    },
    [
      open,
      advance,
    ]
  );


  if (
    !open ||
    !advance
  ) {

    return null;

  }


  const employee =
    advance?.employeeId &&
    typeof advance.employeeId ===
      "object"
      ?
      advance.employeeId
      :
      advance?.employee ||
      {};


  const handleSubmit =
    async (
      event
    ) => {

      event.preventDefault();


      const amount =
        Number(
          form.amount
        );


      if (
        !Number.isFinite(
          amount
        ) ||
        amount <=
        0
      ) {

        setError(
          "Enter a valid recovery amount."
        );

        return;

      }


      if (
        amount >
        balanceAmount
      ) {

        setError(
          "Recovery amount cannot exceed the outstanding balance."
        );

        return;

      }


      try {

        setLoading(
          true
        );


        setError(
          ""
        );


        const response =
          await api.post(
            config.recoverEmployeeAdvance(
              advance._id
            ),
            {
              amount,

              recoveryDate:
                form.recoveryDate,

              recoveryMode:
                form.recoveryMode,

              referenceNumber:
                form.referenceNumber
                  .trim(),

              remark:
                form.remark
                  .trim(),
            }
          );


        if (
          response?.status !==
          "success"
        ) {

          throw new Error(
            response?.message ||
            "Unable to recover advance."
          );

        }


        await onSuccess?.(
          response?.data
        );

      } catch (
        requestError
      ) {

        setError(
          getEmployeeApiError(
            requestError,
            "Unable to recover advance."
          )
        );

      } finally {

        setLoading(
          false
        );

      }

    };


  return ReactDOM
    .createPortal(

      <div className="employee-advance-modal-root">

        <button
          type="button"
          className="employee-advance-modal-backdrop"
          onClick={
            loading
              ?
              undefined
              :
              onClose
          }
          aria-label="Close"
        />


        <form
          className="employee-advance-modal employee-advance-recovery-modal"
          onSubmit={
            handleSubmit
          }
        >

          <header>

            <div>

              <span>
                <FiRotateCcw />
              </span>


              <div>

                <small>
                  ADVANCE RECOVERY
                </small>

                <h3>
                  Recover Advance
                </h3>

              </div>

            </div>


            <button
              type="button"
              onClick={
                onClose
              }
            >
              <FiX />
            </button>

          </header>


          <div className="employee-advance-modal-body">

            <div className="employee-advance-recovery-summary">

              <div>

                <span>
                  Employee
                </span>

                <strong>
                  {
                    employee?.name ||
                    advance?.employeeName ||
                    "-"
                  }
                </strong>

              </div>


              <div>

                <span>
                  Advance
                </span>

                <strong>
                  {
                    formatEmployeeCurrency(
                      advanceAmount
                    )
                  }
                </strong>

              </div>


              <div>

                <span>
                  Recovered
                </span>

                <strong>
                  {
                    formatEmployeeCurrency(
                      recoveredAmount
                    )
                  }
                </strong>

              </div>


              <div className="pending">

                <span>
                  Balance
                </span>

                <strong>
                  {
                    formatEmployeeCurrency(
                      balanceAmount
                    )
                  }
                </strong>

              </div>

            </div>


            <div className="employee-advance-form-grid">

              <div className="employee-advance-form-field">

                <label>
                  Recovery Amount *
                </label>

                <input
                  type="text"
                  inputMode="decimal"
                  value={
                    form.amount
                  }
                  onChange={
                    (
                      event
                    ) =>
                      setForm(
                        (
                          previous
                        ) => ({
                          ...previous,

                          amount:
                            event
                              .target
                              .value
                              .replace(
                                /[^\d.]/g,
                                ""
                              ),
                        })
                      )
                  }
                />

              </div>


              <div className="employee-advance-form-field">

                <label>
                  Recovery Date *
                </label>

                <input
                  type="date"
                  value={
                    form.recoveryDate
                  }
                  onChange={
                    (
                      event
                    ) =>
                      setForm(
                        (
                          previous
                        ) => ({
                          ...previous,

                          recoveryDate:
                            event
                              .target
                              .value,
                        })
                      )
                  }
                />

              </div>


              <div className="employee-advance-form-field">

                <label>
                  Recovery Mode
                </label>

                <select
                  value={
                    form.recoveryMode
                  }
                  onChange={
                    (
                      event
                    ) =>
                      setForm(
                        (
                          previous
                        ) => ({
                          ...previous,

                          recoveryMode:
                            event
                              .target
                              .value,
                        })
                      )
                  }
                >

                  {
                    SALARY_PAYMENT_MODES.map(
                      (
                        mode
                      ) => (

                        <option
                          key={
                            mode
                          }
                          value={
                            mode
                          }
                        >
                          {
                            formatEmployeeLabel(
                              mode
                            )
                          }
                        </option>

                      )
                    )
                  }

                </select>

              </div>


              <div className="employee-advance-form-field">

                <label>
                  Reference Number
                </label>

                <input
                  type="text"
                  value={
                    form.referenceNumber
                  }
                  onChange={
                    (
                      event
                    ) =>
                      setForm(
                        (
                          previous
                        ) => ({
                          ...previous,

                          referenceNumber:
                            event
                              .target
                              .value,
                        })
                      )
                  }
                />

              </div>


              <div className="employee-advance-form-field employee-advance-form-full">

                <label>
                  Remark
                </label>

                <textarea
                  value={
                    form.remark
                  }
                  onChange={
                    (
                      event
                    ) =>
                      setForm(
                        (
                          previous
                        ) => ({
                          ...previous,

                          remark:
                            event
                              .target
                              .value,
                        })
                      )
                  }
                />

              </div>

            </div>


            {
              error
                ?
                (
                  <div className="employee-advance-modal-error">
                    {error}
                  </div>
                )
                :
                null
            }

          </div>


          <footer>

            <button
              type="button"
              onClick={
                onClose
              }
            >
              Cancel
            </button>


            <button
              type="submit"
              disabled={
                loading
              }
            >

              {
                loading
                  ?
                  "Recovering..."
                  :
                  "Save Recovery"
              }

            </button>

          </footer>

        </form>

      </div>,

      document.body

    );

};


export default AdvanceRecoveryModal;